import React, { useCallback, useEffect, useState } from "react";

import { Pen } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import AlertModal from "@/components/shared/AlertModal";
import SettingRow from "@/components/shared/SettingRow";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { ComplianceService } from "@/services/complianceService";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import { complianceSettingsSchema, zodErrorMessages } from "@/pages/admin/compliance/validation.schema";

/**
 * A bounded whole number with its unit beside it.
 *
 * All three settings are this exact control and differ only in bound and unit,
 * so they share one component rather than three near-identical blocks. The unit
 * moves out of the label ("Review SLA (days)") and next to the field, which is
 * what lets the titles read as settings in the rows.
 *
 * `aria-label` is explicit: SettingRow renders the title as a `<span>`, not a
 * `<label>`, so nothing associates the visible title with this input.
 */
const NumberSetting = ({ label, value, onChange, disabled, max, unit }) => (
  <div className="flex items-center gap-2">
    <Input
      type="number"
      min={0}
      max={max}
      step={1}
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg bg-background-form/40 border border-border-primary px-2 py-1.5 text-[13px] text-text-primary w-[6.5rem] disabled:opacity-60 disabled:cursor-not-allowed"
    />
    <span className="text-[13px] text-text-secondary">{unit}</span>
  </div>
);

const DEFAULTS = {
  approvalThreshold: "100",
  reviewSlaDays: "3",
  candidateStuckDays: "3",
};

/**
 * Tenant compliance settings: the weighted approval threshold and the two
 * day-thresholds.
 *
 * Three settings, not five. "Start compliance at status" and "Status after
 * compliance rejection" were removed in Phase S2: candidate status is becoming
 * dynamic and per-job, so a tenant-wide status id no longer names one point in
 * one pipeline. Compliance now engages on whether a run is live (Phase S1), and
 * nothing replaces the two settings — that is the deliverable, not an omission.
 *
 * The two day fields are separate policies and must not be conflated: the
 * review SLA bounds how long an OFFICER may take on an already-submitted
 * document, while the candidate stuck threshold bounds how long a CANDIDATE may
 * leave a re-requested document unanswered. Different party, different clock.
 *
 * **Home: Org Settings** (Phase 9). It sat on the Checklists page while this
 * page was still being built, which its previous docstring flagged as
 * temporary. Being self-contained — it depends only on getSettings /
 * updateSettings, never on host-page state — is what made the move a re-mount.
 *
 * ## Why it carries its own guards
 *
 * The Checklists page used to supply two things this card relied on without
 * owning: a `COMPLIANCE_MANAGE` route guard, and the module notice explaining a
 * fleet-wide kill. Org Settings opens on `TENANT_CONFIG_VIEW`/`EDIT`, a
 * different permission held by different people, so inheriting the host page's
 * gate would have been wrong in both directions — exposing compliance policy to
 * a tenant-config viewer with no compliance rights, and hiding it from the
 * compliance managers it belongs to. So the card gates itself:
 *
 * - `PermissionGuard` with `isComponent` renders **nothing** for a viewer
 *   without `COMPLIANCE_MANAGE`, rather than the full-page `Unauthorized` that
 *   would otherwise replace the whole Org Settings screen. Because the guard
 *   returns null *around* this component, the fetch below never runs either.
 * - `module` on that same guard drops the card for a tenant not entitled to
 *   Compliance at all — settings for a module they do not have explain nothing.
 * - `ModuleUnavailableNotice` covers the third state: entitled but switched off
 *   fleet-wide. The values stay readable and the controls lock, which is what
 *   the kill switch promises.
 *
 * These settings drive compliance policy across the whole tenant (when a
 * candidate counts as approved, how long each side may take), so the card is
 * read-only by default: an explicit Edit unlocks all fields together, and Save
 * is gated behind a confirmation dialog (a save recomputes every candidate's
 * status backend-side).
 */
const ComplianceSettingsCardInner = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Working values (edited in the inputs).
  // Held as a string so the input can be cleared while typing; validated on save.
  const [approvalThreshold, setApprovalThreshold] = useState(DEFAULTS.approvalThreshold);
  // Review turnaround SLA in days (Phase 2B) — how long an OFFICER may take on
  // an already-submitted document. Drives analytics SLA breaches. Held as a
  // string for the same reason.
  const [reviewSlaDays, setReviewSlaDays] = useState(DEFAULTS.reviewSlaDays);
  // How long a CANDIDATE may leave a re-request unanswered before the
  // dashboard's "stuck" worklist surfaces them. A separate policy from the
  // review SLA — different party, different clock.
  const [candidateStuckDays, setCandidateStuckDays] = useState(DEFAULTS.candidateStuckDays);
  // Last-saved snapshot, used to revert on Cancel.
  const [saved, setSaved] = useState(DEFAULTS);

  const applySettings = useCallback((res) => {
    const threshold = res?.settings?.candidateApprovalThreshold;
    const sla = res?.settings?.reviewSlaDays;
    const stuck = res?.settings?.candidateStuckDays;
    const next = {
      approvalThreshold: threshold !== undefined && threshold !== null ? String(threshold) : DEFAULTS.approvalThreshold,
      reviewSlaDays: sla !== undefined && sla !== null ? String(sla) : DEFAULTS.reviewSlaDays,
      candidateStuckDays: stuck !== undefined && stuck !== null ? String(stuck) : DEFAULTS.candidateStuckDays,
    };
    setApprovalThreshold(next.approvalThreshold);
    setReviewSlaDays(next.reviewSlaDays);
    setCandidateStuckDays(next.candidateStuckDays);
    setSaved(next);
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      // One read. The second call here fetched the trigger-status options for
      // a dropdown Phase S2 removed.
      applySettings(await ComplianceService.getSettings());
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessageFormat(e, "Failed to load compliance settings"));
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Open the confirmation dialog after validating the working values. The
  // schema is shared with the rest of the module so these bounds are stated
  // in exactly one place.
  const requestSave = () => {
    const parsed = complianceSettingsSchema.safeParse({
      approvalThreshold,
      reviewSlaDays,
      candidateStuckDays,
    });
    if (!parsed.success) {
      toast.error(zodErrorMessages(parsed.error)[0] ?? "Please check the settings values");
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false);
    const threshold = Number(approvalThreshold);
    const sla = Number(reviewSlaDays);
    const stuck = Number(candidateStuckDays);
    setSaving(true);
    try {
      const res = await ComplianceService.updateSettings({
        candidateApprovalThreshold: threshold,
        reviewSlaDays: sla,
        candidateStuckDays: stuck,
      });
      applySettings(res);
      setEditing(false);
      toast.success("Compliance settings saved");
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessageFormat(e, "Failed to save compliance settings"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setApprovalThreshold(saved.approvalThreshold);
    setReviewSlaDays(saved.reviewSlaDays);
    setCandidateStuckDays(saved.candidateStuckDays);
    setEditing(false);
  };

  // Fields are only editable in edit mode, and never while Compliance is
  // switched off fleet-wide — the save would answer 503. The values still
  // render, which is the whole promise of the kill switch.
  const { isAvailable } = useModuleAccess(MODULES.COMPLIANCE);
  const writesBlocked = !isAvailable;
  const disabled = loading || saving || !editing || writesBlocked;

  return (
    <Card className="p-6 bg-background-secondary border border-border-primary rounded-xl">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-lg font-semibold text-text-primary">Compliance</h2>
            <p className="text-[13px] text-text-secondary leading-relaxed">
              Tenant-wide compliance policy: how much of a checklist must be approved before a candidate counts as compliant, and how long each side may take.
              Changing these re-evaluates every candidate's approval status.
            </p>
            {editing && <p className="text-[12px] text-tone-amber">Editing — changes are not saved until you click Save.</p>}
          </div>
          {editing ? (
            // Save/Cancel live where Edit was, not at the foot of the
            // settings list. Down at the bottom they read as an
            // afterthought below already-live-looking fields — easy to
            // scroll past and easy to mistake for autosave. Anchored in
            // the header, they stay in view the instant editing starts.
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={requestSave}
                disabled={loading || saving || writesBlocked}
                className="bg-button-secondary text-text-base rounded-lg px-4 py-1 border border-button-secondary text-[11px] whitespace-nowrap disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                onClick={handleCancel}
                disabled={saving}
                className="bg-tone-rose-soft text-tone-rose rounded-lg px-4 py-1 text-[11px] whitespace-nowrap border border-tone-rose disabled:opacity-50"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setEditing(true)}
              disabled={loading || saving || writesBlocked}
              className="bg-button-secondary text-text-base rounded-lg px-4 py-1 border border-button-secondary flex items-center gap-1 text-[11px] whitespace-nowrap disabled:opacity-50 flex-shrink-0"
            >
              <Pen size={12} /> Edit
            </Button>
          )}
        </div>

        {/* Entitled but killed fleet-wide: the values still render and
                    the controls lock. Placed once, at the top of the card, the
                    way ModuleUnavailableNotice is meant to be used. */}
        <ModuleUnavailableNotice module={MODULES.COMPLIANCE} suffix="These settings stay readable, and none of them are changed." />

        {/* One SettingRow per setting, matching Consent Confirmation
                    Emails directly above: what the setting is on the left, the
                    control on the right. These were previously wrapped into a
                    single flex row, which read as a form rather than as
                    settings and gave each policy no room to explain itself. */}
        <SettingRow
          title="Candidate approval threshold"
          description="A candidate is approved once this percentage of the checklist's document weight is approved."
          control={
            <NumberSetting label="Candidate approval threshold" value={approvalThreshold} onChange={setApprovalThreshold} disabled={disabled} max={100} unit="%" />
          }
        />

        <SettingRow
          title="Review SLA"
          description="How long your team may take to review a submitted document. Drives analytics SLA breaches."
          control={<NumberSetting label="Review SLA" value={reviewSlaDays} onChange={setReviewSlaDays} disabled={disabled} max={365} unit="days" />}
        />

        <SettingRow
          title="Candidate stuck threshold"
          description="How long a candidate may leave a re-requested document unanswered before the dashboard flags them as stuck."
          control={
            <NumberSetting label="Candidate stuck threshold" value={candidateStuckDays} onChange={setCandidateStuckDays} disabled={disabled} max={365} unit="days" />
          }
        />
      </div>

      <AlertModal
        open={confirmOpen}
        title="Save compliance configuration?"
        description="Are you sure you want to make these changes? This will affect the compliance configuration throughout the system and re-evaluate every candidate's approval status."
        actionText="Yes, save changes"
        cancelText="Cancel"
        onOpenChange={(o) => !o && setConfirmOpen(false)}
        onAction={handleConfirmSave}
        onCancel={() => setConfirmOpen(false)}
        actionClassName="bg-button-secondary text-text-base"
      />
    </Card>
  );
};

/**
 * The guards, kept outside the component that fetches.
 *
 * `isComponent` is the load-bearing prop: without it PermissionGuard renders
 * the full-page `Unauthorized`, which on a shared settings page would blank
 * every *other* card too. With it, a viewer who lacks COMPLIANCE_MANAGE — or a
 * tenant not entitled to Compliance — simply does not see this card, and the
 * inner component never mounts, so `getSettings` is never called on their
 * behalf.
 */
const ComplianceSettingsCard = () => (
  <PermissionGuard permission={PERMISSIONS.COMPLIANCE_MANAGE} module={MODULES.COMPLIANCE} isComponent>
    <ComplianceSettingsCardInner />
  </PermissionGuard>
);

export default ComplianceSettingsCard;
